import useRisksmartUser from '@risksmart-app/components/src/hooks/useRisksmartUser';
import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import { useTools } from '@risksmart-app/components/src/tools/useTools';
import { AssessmentActivityStatus as Assessment_Activity_Status_Enum } from '@risksmart-app/domain/src/types/consts/assessment-activity-status';
import { AssessmentActivityType as Assessment_Activity_Type_Enum } from '@risksmart-app/domain/src/types/consts/assessment-activity-type';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useUpdateAssessmentActivityWithLinkedItems } from '@/hooks/mutations/assessment-activity/useUpdateAssessmentActivityWithLinkedItems';
import { useDeleteWizard } from '@/hooks/mutations/wizard';
import { handleError } from '@/utils/errorUtils';

import { useWizardStore } from '../store/useWizardStore';
import { StepStatus } from '../types';

export const useCloseWizard = (riskId: string) => {
  const {
    setCurrentStep,
    steps,
    setSteps,
    risk,
    assessmentId,
    activityId,
    setWizardStatus,
    setAssessmentId,
    setActivityId,
  } = useWizardStore();
  const [_, setToolsContent] = useTools();
  const { t } = useTranslation('common', { keyPrefix: 'wizard' });
  const { user } = useRisksmartUser();
  const { addNotification } = useNotifications();

  const [loading, setIsLoading] = useState(false);

  const { deleteWizard } = useDeleteWizard();

  const { updateAssessmentActivity } =
    useUpdateAssessmentActivityWithLinkedItems();

  return {
    closeWizard: async (isLastStep = false) => {
      const inProgressStep = steps?.findIndex(
        (step) => step.status === StepStatus.InProgress
      );
      if (isLastStep) {
        setIsLoading(true);
        try {
          await deleteWizard({ RiskId: riskId });
        } catch (error) {
          handleError(error);
          addNotification({
            type: 'error',
            content: (
              <>{error instanceof Error ? error.message : 'Unknown error'}</>
            ),
          });
        }
        // Mirrors the deleteWizard branch above: a rejection here used to escape
        // the async `closeWizard` entirely, so neither `setIsLoading(false)` nor
        // the `setToolsContent(undefined)` at the end of this function ever ran —
        // the Finish button spun forever and the help panel stayed open with no
        // indication of what went wrong (COI-839).
        try {
          await updateAssessmentActivity({
            Id: activityId,
            Title: `${t('wizardName')} - ${risk.title}`,
            Status: Assessment_Activity_Status_Enum.Complete,
            ActivityType: Assessment_Activity_Type_Enum.Task,
            Summary: `${t('wizardName')} - ${risk.title}`,
            // COI-683: the risk↔activity relationship is the `RiskId` column,
            // projected into `linked_item` by migration 1786699284000. Sending
            // the risk here as well wrote the pair twice — the PK is
            // `(Source, Target)` and therefore directional, so both rows
            // persisted and the risk's Linked Items tab listed the activity
            // twice.
            LinkedItemIds: [],
            ParentId: assessmentId,
            CompletionDate: new Date().toISOString(),
            IsWizardAction: true,
            OwnerUserIds: [user!.userId],
            OwnerGroupIds: [],
          });
        } catch (error) {
          handleError(error);
          addNotification({
            type: 'error',
            content: (
              <>{error instanceof Error ? error.message : 'Unknown error'}</>
            ),
          });
        }
        setSteps(t('steps'));
        setCurrentStep(0);
        setWizardStatus(null);
        setAssessmentId('');
        setActivityId('');
        setIsLoading(false);
      } else {
        setCurrentStep(inProgressStep);
      }

      setToolsContent(undefined);
    },
    loading,
  };
};
